/**
 * Modal component for async summary generation
 * Shows progress and status while generating trip summary
 */

import React from 'react';
import { X, Loader, CheckCircle, XCircle, Clock } from 'lucide-react';

interface SummaryGenerationModalProps {
  isOpen: boolean;
  progress: number; // 0-100
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string | null;
  onCancel: () => void;
  onClose: () => void; // Only available when completed/failed
}

const SummaryGenerationModal: React.FC<SummaryGenerationModalProps> = ({
  isOpen,
  progress,
  status,
  error,
  onCancel,
  onClose,
}) => {
  if (!isOpen) return null;

  const canClose = status === 'completed' || status === 'failed';
  const isInProgress = status === 'pending' || status === 'processing';

  const getStatusIcon = () => {
    switch (status) {
      case 'pending':
        return <Clock className="w-12 h-12 text-blue-500 animate-pulse" />;
      case 'processing':
        return <Loader className="w-12 h-12 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-12 h-12 text-green-500" />;
      case 'failed':
        return <XCircle className="w-12 h-12 text-red-500" />;
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'pending':
        return 'Starting Generation...';
      case 'processing':
        return 'Generating Trip Summary';
      case 'completed':
        return 'Summary Generated!';
      case 'failed':
        return 'Generation Failed';
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'pending':
        return 'Preparing your trip data...';
      case 'processing':
        return 'Our AI is analyzing your itinerary and creating a comprehensive summary. This may take up to 2 minutes for long trips.';
      case 'completed':
        return 'Your trip summary has been generated successfully!';
      case 'failed':
        return error || 'An error occurred while generating the summary. Please try again.';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        {/* Modal */}
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 relative">
          {/* Close button (only when completed/failed) */}
          {canClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          )}

          {/* Content */}
          <div className="flex flex-col items-center text-center">
            {/* Icon */}
            <div className="mb-4">
              {getStatusIcon()}
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {getStatusTitle()}
            </h2>

            {/* Message */}
            <p className="text-gray-600 mb-6">
              {getStatusMessage()}
            </p>

            {/* Progress bar (only show when in progress) */}
            {isInProgress && (
              <div className="w-full mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Progress
                  </span>
                  <span className="text-sm font-medium text-blue-600">
                    {progress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-600 h-2.5 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 w-full">
              {isInProgress && (
                <button
                  onClick={onCancel}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
              )}

              {canClose && (
                <button
                  onClick={onClose}
                  className={`flex-1 px-4 py-2.5 font-medium rounded-lg transition-colors ${
                    status === 'completed'
                      ? 'bg-green-500 hover:bg-green-600 text-white'
                      : 'bg-red-500 hover:bg-red-600 text-white'
                  }`}
                >
                  {status === 'completed' ? 'View Summary' : 'Close'}
                </button>
              )}
            </div>

            {/* Additional info for long waits */}
            {status === 'processing' && progress < 50 && (
              <p className="text-xs text-gray-400 mt-4">
                Large trips with many places may take longer to process
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SummaryGenerationModal;

